import { AIAssistantProvider } from "@jugl-web/domain-resources/ai-assistant/providers";
import { OverlayQueueProvider } from "@jugl-web/domain-resources/common/providers/OverlayQueueProvider";
import { liveUpdateEvents$ } from "@jugl-web/domain-resources/live-updates";
import { ActiveTimeTrackingProvider } from "@jugl-web/domain-resources/task-time-tracking/providers/ActiveTimeTrackingProvider";
import { TaskLiveUpdatesProvider } from "@jugl-web/domain-resources/tasks/components/TaskLiveUpdatesProvider";
import {
  cx,
  useAppVariant,
  usePrevious,
  useTabIsActive,
} from "@jugl-web/utils";
import { CXHomeSidebar } from "@web-src/modules/cx/components/CXHomeSidebar";
import { EntityModulesChangesDialog } from "@web-src/components/EntityModulesChangesDialog";
import IncomingCallBanner from "@web-src/components/IncomingCallBanner";
import { LiveUpdatesListener } from "@web-src/components/LiveUpdatesListener";
import { UnreadIndicatorsProvider } from "@web-src/components/UnreadIndicatorsProvider";
import { environment } from "@web-src/environments/environment";
import { useApi } from "@web-src/features/api/useApi";
import { HomeSidebar } from "@web-src/features/app/components/HomeSidebar";
import { MainNavBar } from "@web-src/features/app/components/MainNavBar";
import { TabRoute } from "@web-src/features/app/types";
import { useUpdateTokenMutation } from "@web-src/features/auth/authApi";
import {
  selectAuthState,
  selectUserId,
} from "@web-src/features/auth/authSlice";
import { useNavigateToTaskDetailsPage } from "@web-src/hooks/useNavigateToTaskDetailsPage";
import { DownloadMobileAppCTA } from "@web-src/modules/common/components/DownloadMobileAppCTA";
import ActiveCallWrapper from "@web-src/modules/conference/pages/ConferencePage/components/ActiveCallWrapper";
import { WelcomeToEntityAlert } from "@web-src/modules/entities/components/WelcomeToEntityAlert";
import { useEntityProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntityProvider";
import { AppRoutes } from "@web-src/modules/navigation/components/AppRoutes";
import { useCurrentPageNavConfig } from "@web-src/modules/navigation/hooks/useCurrentPageNavConfig";
import { useFCM } from "@web-src/modules/notifications/providers/FCMProvider/useFCM";
import { usePhoenixSocket } from "@web-src/modules/socket/providers/usePhoenixSocket";
import { useSnackbar } from "notistack";
import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useMatch } from "react-router-dom";
import { useNetworkState } from "react-use";
import { useAnonymousSession } from "@web-src/modules/auth/providers/AnonymousSessionProvider";
import { KnowledgeTrainingProvider } from "@web-src/modules/cx/providers/KnowledgeTrainingProvider";

const App: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { isAuthenticated } = useSelector(selectAuthState);
  const meId = useSelector(selectUserId);
  const { isAnonymous } = useAnonymousSession();
  const navigateToTaskDetailsPage = useNavigateToTaskDetailsPage();
  const { isCx } = useAppVariant();
  const currentPageNavConfig = useCurrentPageNavConfig();
  const {
    showWriteRestrictedAlert,
    subscriptionInfo,
    entity,
    showPlanLimitsReachedAlert,
  } = useEntityProvider();

  useApi({
    onError: (error) => {
      if (subscriptionInfo && !subscriptionInfo?.planIsActive) {
        return;
      }
      if (error.data?.errors === "quota_limit_exceeded") {
        // NOTE: order form submit is handled separately
        if (
          error.url?.includes("/order/form") &&
          error.url?.includes("/submit")
        ) {
          return;
        }
        if (error.url?.includes("/share_link")) {
          showPlanLimitsReachedAlert("clientUpdates");
        } else if (error.url?.includes("/task")) {
          showPlanLimitsReachedAlert("tasks");
        } else if (error.url?.includes("/order/form")) {
          showPlanLimitsReachedAlert("orderForms");
        } else {
          showPlanLimitsReachedAlert("default");
        }
        return;
      }
      if (error.data?.error === "write_restricted") {
        showWriteRestrictedAlert();
        return;
      }
      enqueueSnackbar(error.message, {
        variant: "error",
        preventDuplicate: true,
      });
    },
  });

  const [updateToken] = useUpdateTokenMutation();
  const { token } = useFCM();

  useEffect(() => {
    if (!token || !isAuthenticated) {
      return;
    }
    updateToken(token);
  }, [updateToken, token, isAuthenticated]);

  const { online } = useNetworkState();

  useEffect(() => {
    // make sure app will be reloaded after user wakes up
    const wakeupWorker = new Worker("/detect-wakeup.js");
    wakeupWorker.onmessage = (ev) => {
      if (ev?.data === "app:wakeup") {
        window.location.reload();
      }
    };
  }, []);

  const { channel, socket } = usePhoenixSocket();
  const tabIsActive = useTabIsActive();
  const previousTabIsActive = usePrevious(tabIsActive);

  const isNavigationVisible = !currentPageNavConfig?.isNavigationHidden;

  useEffect(() => {
    if (tabIsActive === previousTabIsActive) {
      return;
    }
    channel?.push(
      "device_status",
      tabIsActive ? { status: null } : { status: "offline" }
    );
  }, [channel, tabIsActive, previousTabIsActive]);
  const $authenticatedContent = useMemo(() => {
    let $content: React.ReactNode = null;
    if (environment.isDemo) {
      $content = (
        <div className="flex h-screen w-screen flex-col">
          <AppRoutes />
        </div>
      );
    } else {
      $content =
        entity && (subscriptionInfo?.planIsActive || isAnonymous) ? (
          <>
            <EntityModulesChangesDialog />
            <ActiveCallWrapper />
            <div className="fixed left-0 top-0 flex h-[100vh] w-[100vw] min-w-0 flex-col">
              {isNavigationVisible && <MainNavBar />}
              <div
                className={cx(
                  "flex min-h-0 min-w-0",
                  isNavigationVisible && "h-[calc(100%-_48px)]"
                )}
              >
                {!currentPageNavConfig?.isSideBarHidden ? (
                  <>{isCx ? <CXHomeSidebar /> : <HomeSidebar />}</>
                ) : null}
                <div className="z-10 h-full w-full min-w-0 grow overflow-y-auto">
                  <AppRoutes />
                </div>
              </div>
            </div>
            <WelcomeToEntityAlert />
          </>
        ) : (
          <AppRoutes />
        );
    }
    if (entity) {
      const contentWithProviders = (
        <KnowledgeTrainingProvider entityId={entity.id}>
          <OverlayQueueProvider>
            <ActiveTimeTrackingProvider
              isAnonymousSession={isAnonymous}
              entityId={entity.id}
              onNavigateToTask={(taskId, taskBoardId) =>
                navigateToTaskDetailsPage({
                  taskId,
                  customTasksSource: taskBoardId
                    ? { type: "boardTasks", boardId: taskBoardId }
                    : undefined,
                })
              }
            >
              {$content}
            </ActiveTimeTrackingProvider>
          </OverlayQueueProvider>
        </KnowledgeTrainingProvider>
      );

      $content = isAnonymous ? (
        contentWithProviders
      ) : (
        <AIAssistantProvider socket={socket} userId={meId} entityId={entity.id}>
          {contentWithProviders}
        </AIAssistantProvider>
      );
    }
    return $content;
  }, [
    entity,
    subscriptionInfo?.planIsActive,
    isAnonymous,
    isNavigationVisible,
    currentPageNavConfig?.isSideBarHidden,
    isCx,
    socket,
    meId,
    navigateToTaskDetailsPage,
  ]);

  const taskDetailsPageMatch = useMatch(
    `/:entityId/${TabRoute.tasks}/details/:taskId`
  );

  const isInTaskDetailsView = !!taskDetailsPageMatch;

  return (
    <>
      {!online && (
        <div className="bg-tertiary-500 fixed left-[50%] top-10 z-[2100] -translate-x-[50%] rounded px-12 py-5 text-center font-bold text-white">
          No internet connection, please reconnect to use Jugl
        </div>
      )}
      <DownloadMobileAppCTA />
      <div className={cx({ "opacity-50": !online })}>
        {isAuthenticated || isAnonymous ? (
          <>
            <IncomingCallBanner />
            <LiveUpdatesListener events$={liveUpdateEvents$} />
            <TaskLiveUpdatesProvider
              events$={liveUpdateEvents$}
              meId={meId}
              isInTaskDetailsView={isInTaskDetailsView}
            >
              <UnreadIndicatorsProvider>
                {$authenticatedContent}
              </UnreadIndicatorsProvider>
            </TaskLiveUpdatesProvider>
          </>
        ) : (
          <AppRoutes />
        )}
      </div>
    </>
  );
};

export default App;
